import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { MascotScene } from "../components/Mascot";
import { tg } from "../lib/telegram";

const NOW = [
  { icon: "👤", titleKey: "welcome.features.profileTitle", descKey: "welcome.features.profileDesc" },
  { icon: "🗓", titleKey: "welcome.features.listTitle", descKey: "welcome.features.listDesc" },
  { icon: "✅", titleKey: "welcome.features.rsvpTitle", descKey: "welcome.features.rsvpDesc" },
  { icon: "⏰", titleKey: "welcome.features.remindersTitle", descKey: "welcome.features.remindersDesc" },
];

const NEXT = [
  { icon: "🌍", titleKey: "welcome.upcoming.langsTitle", descKey: "welcome.upcoming.langsDesc" },
  { icon: "✨", titleKey: "welcome.upcoming.eventsTitle", descKey: "welcome.upcoming.eventsDesc" },
];

export function Welcome() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const app = tg();
    if (!app) return;
    app.MainButton.setText(t("welcome.createProfile"));
    app.MainButton.show();
    const handler = () => {
      app.HapticFeedback?.impactOccurred("medium");
      navigate("/register");
    };
    app.MainButton.onClick(handler);
    return () => {
      app.MainButton.offClick(handler);
      app.MainButton.hide();
    };
  }, [navigate, t]);

  return (
    <div className="min-h-screen bg-surface">
      {/* Hero with soft gradient */}
      <div className="relative overflow-hidden pt-6 pb-8 px-5">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, var(--color-brand-purple-soft) 0%, transparent 70%)",
          }}
        />
        <div className="flex justify-center mb-2">
          <MascotScene width={300} />
        </div>
        <h1 className="text-[28px] font-extrabold text-ink-900 leading-[1.15] text-center mt-4 tracking-tight">
          {t("welcome.heroTitle")}
        </h1>
        <p className="text-[15px] text-ink-500 text-center mt-3 leading-relaxed max-w-[320px] mx-auto">
          {t("welcome.heroText")}
        </p>
      </div>

      {/* Now section */}
      <div className="px-5 mt-6">
        <SectionLabel>{t("welcome.whatWorks")}</SectionLabel>
        <div className="space-y-2">
          {NOW.map((it, i) => (
            <FeatureRow key={i} icon={it.icon} title={t(it.titleKey)} desc={t(it.descKey)} />
          ))}
        </div>
      </div>

      {/* Next section */}
      <div className="px-5 mt-8">
        <SectionLabel>{t("welcome.whereGoing")}</SectionLabel>
        <div className="space-y-2 opacity-80">
          {NEXT.map((it, i) => (
            <FeatureRow key={i} icon={it.icon} title={t(it.titleKey)} desc={t(it.descKey)} />
          ))}
        </div>
      </div>

      {/* CTA fallback for clients without MainButton */}
      <div className="px-5 mt-8 pb-10">
        <Button stretched size="lg" onClick={() => navigate("/register")}>
          {t("welcome.createProfile")}
        </Button>
        <p className="text-center text-[12px] text-ink-400 mt-4">
          {t("welcome.footer")}
        </p>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-400 mb-3">
      {children}
    </div>
  );
}

function FeatureRow({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="flex gap-3 p-3 rounded-[var(--radius-md)] bg-ink-50">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-surface flex items-center justify-center text-[20px]">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-ink-900 leading-tight">{title}</div>
        <div className="text-[13px] text-ink-500 mt-0.5 leading-snug">{desc}</div>
      </div>
    </div>
  );
}
